/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RoleListPage
 *
 * React page that renders the full server-side paginated and searchable
 * list of application roles. Includes:
 * - React Query–powered GET request to `/arcee/user/roles/<app_id>/`
 * - Server-driven pagination, search, and result counts
 * - Error formatting and unified AlertDialog for load failures
 * - ProgressBarDialog shown during initial fetch
 * - Navigation helpers for creating, editing, or deleting roles
 *
 * The page delegates UI rendering to `RoleListComponent` while managing all
 * API calls, global state, and routing behavior for application-level role management.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import RoleListComponent from '../components/applications/RoleListComponent';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';
import { useAlertDialog } from '../components/common/AlertDialogContext';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const DEFAULT_PAGE_SIZE = 10;

export default function RoleListPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id: applicationId } = useParams();
  const { showAlertDialog } = useAlertDialog();
  const queryClient = useQueryClient();
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = React.useState('');
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState('info');

  const { data: rolesData, isLoading, isError, error } = useQuery({
    queryKey: ['roles', applicationId, page, pageSize, search],
    queryFn: async ({ signal }) => {
      if (!applicationId) return { results: [], count: 0 };

      const url = `/arcee/user/roles/${applicationId}/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`;

      const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
      if (!resp.ok) {
        let errBody;
        try { errBody = await resp.json(); } catch { errBody = await resp.text().catch(() => null); }
        const e = new Error('Failed to fetch roles');
        e.info = errBody;
        throw e;
      }

      const body = await resp.json().catch(() => null);
      if (Array.isArray(body)) return { results: body, count: body.length };
      return {
        results: body?.results ?? body ?? [],
        count: body?.count ?? (Array.isArray(body) ? body.length : 0),
        application_id: body?.application_id,
        application_name: body?.application_name
      };
    },
    enabled: !!applicationId,
    placeholderData: keepPreviousData,
    staleTime: 5000,
    cacheTime: 2 * 60 * 1000,
    retry: 1,
  });

  // Format API errors to string for AlertDialog
  function formatErrorMessage(err) {
    if (!err) return 'An unknown error occurred.';
    if (err.info) {
      if (typeof err.info === 'string') return err.info;
      if (typeof err.info === 'object') {
        const parts = [];
        for (const [k, v] of Object.entries(err.info)) {
          if (Array.isArray(v)) parts.push(`${k}: ${v.join(' ')}`);
          else parts.push(`${k}: ${String(v)}`);
        }
        return parts.join('\n');
      }
    }
    return err.message || String(err);
  }

  React.useEffect(() => {
    if (isError) {
      setAlertTitle('Failed to load roles');
      setAlertMessage(formatErrorMessage(error));
      setAlertType('error');
      setAlertOpen(true);
    }
  }, [isError, error]);

  const rolesList = rolesData?.results ?? [];
  const totalCount = rolesData?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const appName = rolesData?.application_name;

  // Handlers for RoleListComponent
  const handleCreate = () => {
    navigate(`/dashboard/applications/${applicationId}/roles/create`);
  };

  const handleEdit = (role) => {
    navigate(`/dashboard/applications/${applicationId}/roles/${role.id}/update`, { state: role });
  };

  /**
   * Deletes a role and adjusts pagination.
   * Steps:
   * 1. Find role's global index in full list.
   * 2. Confirm deletion with user.
   * 3. Call backend DELETE API.
   * 4. On success → navigate to page of previous record.
   * 5. Refresh list and close progress bar.
   */
  const handleDeleteRole = async (role) => {
    const indexInPage = rolesList.findIndex((r) => r.id === role.id);
    const globalIndex = (page - 1) * pageSize + indexInPage + 1;

    showAlertDialog({
      title: "Confirm Deletion",
      message: `Are you sure you want to delete role "${role.name}"?`,
      type: "info",
      okLabel: "Delete",
      cancelLabel: "Cancel",
      showCancel: true,

      onOk: async () => {
        setProgressOpen(true);
        setProgressMessage("Deleting user role...");

        try {
          const resp = await apiHelper.apiCall(`/arcee/user/roles/${role.id}/delete/`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          });

          if (!resp.ok) {
            const errBody = await resp.json().catch(() => null);
            showAlertDialog({
              title: "Deletion Failed",
              message: errBody?.error || errBody?.message || "Could not delete the role.",
              type: "error",
              okLabel: "Close",
              showCancel: false,
            });
            return;
          }

          const newIndex = Math.max(globalIndex - 2, 0);
          const newPage = Math.floor(newIndex / pageSize) + 1;

          showAlertDialog({
            title: "Success",
            message: "User role deleted successfully.",
            type: "success",
            okLabel: "OK",
            showCancel: false,

            onOk: async () => {
              setPage(newPage);
              await queryClient.invalidateQueries(['roles', applicationId]);
            }
          });

        } catch (error) {
          showAlertDialog({
            title: "Error",
            message: error.message || "Network error during deletion.",
            type: "error",
            okLabel: "Close",
            showCancel: false,
          });

        } finally {
          setProgressOpen(false);
        }
      },
    });
  };

  // When user changes page from component
  const handlePage = (p) => {
    setPage(p);
  };

  return (
    <>
      {isLoading ? (
        <ProgressBarDialog open={true} text_message="Loading roles..." cancellable={false} />
      ) : (
        <RoleListComponent
          appName={appName}
          roles={rolesList}
          page={page}
          pageCount={pageCount}
          search={search}
          onSearch={(val) => { setPage(1); setSearch(val); }}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDeleteRole}
          onPage={handlePage}
        />
      )}

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        okLabel="OK"
        showCancel={false}
        onOk={() => setAlertOpen(false)}
        mini_mode={false}
      />

      <ProgressBarDialog
        open={progressOpen}
        text_message={progressMessage}
        cancellable={false}
      />
    </>
  );
}
